import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress, Box, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { initiateMyLoginAuth } from '../../utils/myLoginAuth';
import MyLoginIcon from '../icons/MyLoginIcon';

const StyledLoginButton = styled(Button)(({ theme, bgcolor, hovercolor, size }) => ({
  backgroundColor: bgcolor || theme.palette.primary.main,
  color: '#fff',
  padding: size === 'small' ? '8px 16px' : size === 'large' ? '16px 24px' : '12px 20px',
  borderRadius: '12px',
  width: '100%',
  fontSize: size === 'small' ? '14px' : size === 'large' ? '18px' : '16px',
  fontWeight: 500,
  textTransform: 'none',
  marginBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: 'flex-start',
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: hovercolor || theme.palette.primary.dark,
    boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)',
  },
  '&:focus': {
    boxShadow: `0 0 0 3px ${theme.palette.primary.light}40`,
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.12)',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[400],
  }
}));

function MyLoginButton({
  redirectAfterLogin,
  label = 'Login with MyLogin',
  bgcolor,
  hovercolor,
  size = 'medium',
  tooltip,
  sx = {},
  ...props
}) {
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    try {
      setLoading(true);
      await initiateMyLoginAuth(redirectAfterLogin);
    } catch (error) {
      console.error('Failed to initiate MyLogin authentication:', error);
      setLoading(false);
    }
  };

  const button = (
    <StyledLoginButton
      onClick={handleLogin}
      disabled={loading}
      bgcolor={bgcolor}
      hovercolor={hovercolor}
      size={size}
      aria-label="Login with MyLogin authentication service"
      sx={{ ...sx }}
      {...props}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
        {loading ? (
          <CircularProgress size={size === 'small' ? 16 : 22} color="inherit" sx={{ mr: 1 }} />
        ) : (
          <MyLoginIcon fontSize={size === 'small' ? 'small' : 'medium'} sx={{ mr: 1.5 }} />
        )}
        {loading ? 'Connecting...' : label}
      </Box>
    </StyledLoginButton>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        {button}
      </Tooltip>
    );
  }

  return button;
}

MyLoginButton.propTypes = {
  redirectAfterLogin: PropTypes.string,
  label: PropTypes.string,
  bgcolor: PropTypes.string,
  hovercolor: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  tooltip: PropTypes.string,
  sx: PropTypes.object,
};

MyLoginButton.displayName = 'MyLoginButton';

export default MyLoginButton;